/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {closeSecondaryModal, openSecondaryModal} from 'helpers/actions'
import {useAppDispatch} from 'helpers/store'
import {CompaniesAction} from 'store/companies/CompaniesAction'
import {OffersAction} from 'store/offers/OffersAction'
import {setDraftOfferId} from 'store/offers/OffersReducer'
import BookingAction from 'store/booking/BookingAction'
import InProgressOfferGuardModal from 'components/Offers/InProgressOfferGuardModal'
import {ModalSize} from 'types/enums/ModalSize'
import {EditLeadModalTab} from 'types/enums/specific/EditLeadModalTab'
import type {Lead} from 'types/entities/Lead'

interface UseEditLeadTabNavigationParams {
    activeTab: EditLeadModalTab
    setActiveTab: React.Dispatch<React.SetStateAction<EditLeadModalTab>>
    isCreateOfferEnabled: boolean
    createOfferInProgressIdRef: React.MutableRefObject<number | null>
    selectedLead: Lead | null | undefined
    onReloadLead: () => Promise<void>
}

export const useEditLeadTabNavigation = ({
    activeTab,
    setActiveTab,
    isCreateOfferEnabled,
    createOfferInProgressIdRef,
    selectedLead,
    onReloadLead,
}: UseEditLeadTabNavigationParams) => {
    const {t} = useTranslation()
    const dispatch = useAppDispatch()

    const doTabSwitch = useCallback((tabId: EditLeadModalTab) => {
        if (tabId === EditLeadModalTab.Offers) {
            void onReloadLead().catch(() => {})
        }
        if (tabId === EditLeadModalTab.Customer) {
            const targetCompanyId = selectedLead?.company_id ?? selectedLead?.company?.id
            dispatch(CompaniesAction.resetSelectedCompany())
            if (targetCompanyId) {
                dispatch(CompaniesAction.fetchCompanyById(targetCompanyId)).catch(() => {})
            }
        }
        setActiveTab(tabId)
        if (tabId === EditLeadModalTab.CreateOffer) {
            dispatch(OffersAction.resetSelectedOffer())
            dispatch(BookingAction.clearBookingProcessState())
        }
    }, [dispatch, selectedLead, setActiveTab, onReloadLead])

    const handleTabClick = useCallback((tabId: EditLeadModalTab) => {
        if (tabId === EditLeadModalTab.CreateOffer && !isCreateOfferEnabled) return

        const leavingWizard =
            activeTab === EditLeadModalTab.CreateOffer &&
            tabId !== EditLeadModalTab.CreateOffer &&
            createOfferInProgressIdRef.current !== null

        if (leavingWizard) {
            const offerId = createOfferInProgressIdRef.current!
            dispatch(
                openSecondaryModal(
                    <InProgressOfferGuardModal
                        offerId={offerId}
                        onDeleteAndLeave={async () => {
                            await dispatch(OffersAction.fetchDeleteOffer(offerId))
                            dispatch(setDraftOfferId(null))
                            createOfferInProgressIdRef.current = null
                            dispatch(closeSecondaryModal())
                            doTabSwitch(tabId)
                        }}
                        onKeepAndLeave={() => {
                            dispatch(setDraftOfferId(offerId))
                            createOfferInProgressIdRef.current = null
                            dispatch(closeSecondaryModal())
                            doTabSwitch(tabId)
                        }}
                        onStay={() => dispatch(closeSecondaryModal())}
                    />,
                    {modalSize: ModalSize.Small},
                    t('Leave Offer Wizard?')
                )
            )
            return
        }

        doTabSwitch(tabId)
    }, [dispatch, activeTab, isCreateOfferEnabled, createOfferInProgressIdRef, doTabSwitch, t])

    return {handleTabClick}
}
